import React, { useState, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../../types/types';
import Icon from '../Icon/Icon';
import styles from './UploadInput.module.scss';

export interface UploadedFile {
  id: string;
  name: string;
  type: string;
  dataUrl: string;
  size?: number;
  fileObject?: File;
  isExisting?: boolean;
  status?: number;
}

interface UploadInputProps {
  onFilesChange: (files: UploadedFile[]) => void;
  acceptedFileTypes?: string;
  maxFiles?: number;
  placeholder?: string;
  size?: 'sm' | 'md';
  initialFiles?: UploadedFile[];
}

export const stripFileExtension = (filename: string): string => {
  return filename.split('.').slice(0, -1).join('.');
};

export const getFileExtension = (filename: string): string => {
  const parts = filename.split('.');
  return parts.length > 1 ? parts.pop()! : '';
};

const UploadInput: React.FC<UploadInputProps> = ({
  onFilesChange,
  acceptedFileTypes = '.pdf,.doc,.docx,.txt,.jpg,.jpeg,.png,.gif,.webp,.svg,.xls,.xlsx,.xlsm,.ppt,.pptx,.pptm',
  maxFiles = 10,
  placeholder = 'Add Attachments',
  size = 'md',
  initialFiles = [],
}) => {
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>(initialFiles);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setUploadedFiles(initialFiles);
  }, [initialFiles]);

  const currentTheme = useSelector((state: RootState) => state.theme.currentTheme);

  const handleFileSelect = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);

    if (uploadedFiles.length + files.length > maxFiles) {
      alert(`Maximum ${maxFiles} files allowed`);
      return;
    }

    const fileReaders: Promise<UploadedFile>[] = files.map((file, index) => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = () => {
          resolve({
            id: `${Date.now()}-${index}`,
            name: stripFileExtension(file.name),
            type: getFileExtension(file.name),
            dataUrl: reader.result as string,
            size: file.size,
            fileObject: file,
          });
        };

        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
    });

    Promise.all(fileReaders).then((newFiles) => {
      const updatedFiles = [...uploadedFiles, ...newFiles];

      setUploadedFiles(updatedFiles);
      onFilesChange(updatedFiles);

      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    });
  };

  const handleRemoveFile = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    fileId: string
  ) => {
    event.stopPropagation();
    const updatedFiles = uploadedFiles.filter((file) => file.id !== fileId);
    setUploadedFiles(updatedFiles);
    onFilesChange(updatedFiles);
  };

  return (
    <div
      className={`${styles.upload_input} ${uploadedFiles.length > 0 ? styles.has_files : ''} ${styles[`size_${size}`]}`}
      onClick={(e) => {
        e.stopPropagation();
        handleFileSelect();
      }}
    >
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={acceptedFileTypes}
        onChange={handleFileChange}
        className={styles.hidden_input}
      />

      <div className={`${styles.upload_header}`}>
        <span className={`${styles.upload_placeholder}`}>{placeholder}</span>

        <div className={styles.header_icons}>
          <button className={`${styles.add_more_button}`} aria-label="Add more files">
            <Icon
              name="plus_big"
              size={20}
              color={currentTheme === 'light' ? '#707070' : '#AEAEB2'}
            />
          </button>
        </div>
      </div>

      {uploadedFiles.length > 0 && (
        <div className={`${styles.files_container}`}>
          {uploadedFiles.map((file) => (
            <div key={file.id} className={`${styles.file_item}`}>
              <div className={styles.file_info}>
                <Icon
                  name="document"
                  size={20}
                  color={currentTheme === 'light' ? '#707070' : '#AEAEB2'}
                />
                <div className={styles.file_details}>
                  <span className={`${styles.file_name}`}>
                    {file.name.length > 15 ? `${file.name.substring(0, 12)}...` : file.name}
                  </span>
                  <span className={`${styles.file_type}`}>{file.type}</span>
                </div>
              </div>

              <button
                onClick={(event) => handleRemoveFile(event, file.id)}
                className={`${styles.remove_button}`}
              >
                <Icon
                  name="cross_icon"
                  size={16}
                  color={currentTheme === 'light' ? '#707070' : '#AEAEB2'}
                />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default UploadInput;
